import { Fragment, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, CalendarDays, CalendarHeart } from "lucide-react";
import { toast } from "sonner";
import { eventBadgeClass, fmtEventRange } from "@/components/EventsList";

const EMPTY = { title: "", type: "Academic", start_date: "", end_date: "", time: "", venue: "", description: "" };

export default function AdminEvents({ embedded } = {}) {
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState(["Academic", "Sports", "Cultural", "Celebration", "PTM", "Excursion", "Competition", "Other"]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | {mode:'create'} | {mode:'edit', id}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events");
      setEvents(data.events || []);
      if (data.types?.length) setTypes(data.types);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load events"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setDialog({ mode: "create" }); };
  const openEdit = (ev) => {
    setForm({
      title: ev.title, type: ev.type, start_date: ev.start_date, end_date: ev.end_date || "",
      time: ev.time || "", venue: ev.venue || "", description: ev.description || "",
    });
    setDialog({ mode: "edit", id: ev.id });
  };

  const save = async () => {
    const title = form.title.trim();
    if (!title) { toast.error("Event title is required."); return; }
    if (!form.start_date) { toast.error("Start date is required."); return; }
    if (form.end_date && form.end_date < form.start_date) { toast.error("End date can't be before start date."); return; }
    setSaving(true);
    const payload = {
      title, type: form.type, start_date: form.start_date,
      end_date: form.end_date || form.start_date,
      time: form.time.trim() || null, venue: form.venue.trim() || null,
      description: form.description.trim() || null,
    };
    try {
      if (dialog.mode === "create") await api.post("/events", payload);
      else await api.patch(`/events/${dialog.id}`, payload);
      toast.success(dialog.mode === "create" ? "Event added." : "Event updated.");
      setDialog(null);
      await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted.");
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Delete failed"); }
    finally { setDeletingId(null); }
  };

  const Wrap = embedded ? Fragment : "div";
  return (
    <Wrap>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Events</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Add, edit or remove school events. Teachers and students see these read-only.</p>
        </div>
        <Button onClick={openCreate} className="bg-sky-500 hover:bg-sky-600" data-testid="event-add-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add event
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="admin-events-empty">
          No events yet. Click “Add event” to create one.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-events-list">
          {events.map((ev) => (
            <div key={ev.id} className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3" data-testid={`admin-event-${ev.id}`}>
              <div className="h-9 w-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
                <CalendarHeart className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 dark:text-slate-100 truncate">{ev.title}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${eventBadgeClass(ev.type)}`}>{ev.type}</span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <CalendarDays className="h-3 w-3" /> {fmtEventRange(ev.start_date, ev.end_date)}
                  {ev.time && <span>· {ev.time}</span>}
                  {ev.venue && <span>· {ev.venue}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="rounded-full" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40" onClick={() => remove(ev.id)} disabled={deletingId === ev.id} data-testid={`event-delete-${ev.id}`}>
                  {deletingId === ev.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "edit" ? "Edit event" : "Add event"}</DialogTitle>
            <DialogDescription>Set the date(s), type and optional details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Title</div>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Annual Sports Day" data-testid="event-title-input" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Type</div>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t} value={t} data-testid={`event-type-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Start date</div>
                <DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} data-testid="event-start-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">End date <span className="font-normal text-slate-400">(optional)</span></div>
                <DatePicker value={form.end_date} min={form.start_date || undefined} onChange={(v) => setForm({ ...form, end_date: v })} placeholder="Same as start" data-testid="event-end-input" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Time <span className="font-normal text-slate-400">(optional)</span></div>
                <Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="e.g., 9:00 AM" data-testid="event-time-input" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Venue <span className="font-normal text-slate-400">(optional)</span></div>
              <Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="e.g., School Auditorium" data-testid="event-venue-input" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Description <span className="font-normal text-slate-400">(optional)</span></div>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Any details for teachers & students" rows={2} data-testid="event-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600" onClick={save} disabled={saving || !form.title.trim() || !form.start_date} data-testid="event-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Wrap>
  );
}
